/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('DOMContentLoaded', function() {
  function watchForButtons(buttonClass, clickHandler) {
    function attachListener(button) {
      if (button.dataset.listenerAttached) return;

      button.addEventListener('click', (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        clickHandler(e);
      }, true);

      button.dataset.listenerAttached = 'true';
    }

    document.querySelectorAll(`.boost-sd__product-item .${buttonClass}`).forEach(attachListener);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          if (node.classList.contains(buttonClass)) {
            attachListener(node);
          }

          node.querySelectorAll(`.boost-sd__product-item .${buttonClass}`).forEach(attachListener);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return observer;
  }

  async function addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const body = {
        id: variantId,
        quantity,
      };

      // Only include properties if there are any
      if (Object.keys(properties).length > 0) {
        body.properties = properties;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Add to cart failed: ${response.statusText}`);

      const data = await response.json();
      console.log('Added to cart:', data);
      return data;

    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  function parseButtonData(button) {
    try {
      const raw = button.dataset.metadata;
      const parsed = JSON.parse(raw);

      const variantId = parsed.action.addToCart.productId;
      const properties = {};

      // Conditionally parse the property field if it exists
      const property = parsed.action.addToCart.property;
      if (property && property.includes('|')) {
        const [key, value] = property.split('|');
        properties[key.trim()] = value.trim();
      }

      return { variantId, properties };

    } catch (error) {
      console.error('Failed to parse metadata attribute:', error);
      return null;
    }
  }

  // Usage
  const observer = watchForButtons('boost-sd__button--select-option', async (e) => {
    const button = e.currentTarget;
    const data = parseButtonData(button);

    if (!data?.variantId) {
      console.error('No variant ID found on button');
      return;
    }

    try {
      // Optional: visual feedback while adding
      button.classList.add('is-loading');

      await addToCart(data.variantId, 1, data.properties);

      // Optional: success feedback
      button.classList.add('is-success');
      setTimeout(() => {
        button.classList.remove('is-loading');
        button.classList.remove('is-success');
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { 
          bubbles: true
        }));
      }, 1400);

    } catch (error) {
      button.classList.remove('is-loading');
      console.error('Could not add item to cart:', error);
    }
  });
});

document.addEventListener('click', function (e) {
  if (e.target.closest('.boost-sd-container-search-page .boost-sd__search-form button[type="reset"]')) {
    e.preventDefault();
    window.location.href = '/search';
  }
});

function initScrollBottomDetector({
  className = 'at-bottom',
  threshold = 2,
  throttleMs = 100,
  } = {}) {
  const root = document.documentElement;
  let throttleTimer = null;
  let pendingCheck = false;

  function checkBottom() {
    const atBottom =
      window.innerHeight + window.scrollY >= document.body.scrollHeight - threshold;
    root.classList.toggle(className, atBottom);
  }

  function onScroll() {
    if (throttleTimer) {
      pendingCheck = true;
      return;
    }

    checkBottom();

    throttleTimer = setTimeout(() => {
      throttleTimer = null;
      if (pendingCheck) {
        pendingCheck = false;
        checkBottom();
      }
    }, throttleMs);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  checkBottom();

  return () => window.removeEventListener('scroll', onScroll);
}

// Usage
const cleanup = initScrollBottomDetector();


(function () {
  const WRAPPER_ID = 'restock-rocket';
  const MESSAGE_IDS = ['form-Error-Message', 'form-Success-Text'];
  const INPUT_IDS = ['form-Email'];
  const ATTRIBUTES = {
    role: 'alert',
    'aria-live': 'assertive',
  };

  function applyAttributes(el) {
    Object.entries(ATTRIBUTES).forEach(([attr, value]) => {
      if (!el.hasAttribute(attr)) {
        el.setAttribute(attr, value);
      }
    });

    // Briefly clear and restore content to give the screen reader a chance
    // to register the live region before the content lands
    const content = el.innerHTML;
    el.innerHTML = '';
    requestAnimationFrame(() => {
      el.innerHTML = content;
    });
  }

  function applyRequired(el) {
    if (!el.hasAttribute('required')) {
      el.setAttribute('required', '');
    }
  }

  function checkAndApply(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    // Check message elements
    if (MESSAGE_IDS.includes(node.id)) {
      applyAttributes(node);
    }
    MESSAGE_IDS.forEach((id) => {
      const el = node.querySelector?.('#' + id);
      if (el) applyAttributes(el);
    });

    // Check input elements
    if (INPUT_IDS.includes(node.id)) {
      applyRequired(node);
    }
    INPUT_IDS.forEach((id) => {
      const el = node.querySelector?.('#' + id);
      if (el) applyRequired(el);
    });
  }

  function observeWrapper(wrapper) {
    // Check for any targets that may already be in the wrapper
    MESSAGE_IDS.forEach((id) => {
      const el = wrapper.querySelector('#' + id);
      if (el) applyAttributes(el);
    });
    INPUT_IDS.forEach((id) => {
      const el = wrapper.querySelector('#' + id);
      if (el) applyRequired(el);
    });

    // Watch for targets being added dynamically inside the wrapper
    const innerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => checkAndApply(node));
      });
    });

    innerObserver.observe(wrapper, { childList: true, subtree: true });
  }

  function init() {
    // If wrapper is already in the DOM, observe it immediately
    const existingWrapper = document.getElementById(WRAPPER_ID);
    if (existingWrapper) {
      observeWrapper(existingWrapper);
      return;
    }

    // Otherwise watch for it to be added
    const outerObserver = new MutationObserver((mutations, obs) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          const wrapper =
            node.id === WRAPPER_ID ? node : node.querySelector?.('#' + WRAPPER_ID);

          if (wrapper) {
            obs.disconnect(); // Stop watching for the wrapper
            observeWrapper(wrapper);
          }
        });
      });
    });

    outerObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

window.addEventListener('load', () => {
  if (window.location.hash) {
    const target = document.querySelector(window.location.hash);
    if (target) target.scrollIntoView();
  }
});

document.addEventListener('DOMContentLoaded', function() {

  /**
   * Boost SD product stickers
   * -------------------------
   * Watches for `.boost-sd__product-item` elements being added to the page,
   * reads the product handle out of their `data-product` JSON, fetches
   * `/products/<handle>?view=data`, and if that response contains a
   * `.card__sticker--group` element, appends it inside the item's
   * `.boost-sd__product-link-image` container.
   *
   * Drop this in as a theme asset and load it with `defer`, e.g.
   *   {{ 'boost-sd-stickers.js' | asset_url | script_tag }}
   *
   * Requires an alternate product template in the theme named
   * `product.data.liquid` that renders (at minimum) the sticker markup.
   */
  (function () {
    'use strict';

    /* ------------------------------------------------------------------ */
    /* Config                                                              */
    /* ------------------------------------------------------------------ */

    var ITEM_SELECTOR    = '.boost-sd__product-item';
    var TARGET_SELECTOR  = '.boost-sd__product-link-image';
    var STICKER_SELECTOR = '.card__sticker--group';

    // Marks items we've already handled so re-renders don't refetch.
    var STATE_ATTR = 'data-sticker-handle';

    var MAX_CONCURRENT_FETCHES = 6; // be kind to the storefront
    var TARGET_WAIT_TRIES      = 20; // ~2s of retries waiting for the image container
    var TARGET_WAIT_INTERVAL   = 100; // ms
    var DEBUG = false;

    function log() {
      if (DEBUG && window.console) console.log.apply(console, ['[stickers]'].concat([].slice.call(arguments)));
    }

    /* ------------------------------------------------------------------ */
    /* Small concurrency queue                                             */
    /* ------------------------------------------------------------------ */

    var active = 0;
    var queue  = [];

    function schedule(task) {
      return new Promise(function (resolve, reject) {
        queue.push({ task: task, resolve: resolve, reject: reject });
        pump();
      });
    }

    function pump() {
      while (active < MAX_CONCURRENT_FETCHES && queue.length) {
        var job = queue.shift();
        active++;
        job.task().then(job.resolve, job.reject).then(function () {
          active--;
          pump();
        });
      }
    }

    /* ------------------------------------------------------------------ */
    /* Handle extraction                                                   */
    /* ------------------------------------------------------------------ */

    /**
     * getAttribute() returns the *decoded* attribute value, so the &quot;
     * entities in the markup are already real quote characters here.
     * The value has a lot of leading/trailing whitespace — JSON.parse
     * doesn't care.
     */
    function getHandle(item) {
      var raw = item.getAttribute('data-product');
      if (!raw) return null;

      try {
        var data = JSON.parse(raw);
        if (data && typeof data.handle === 'string' && data.handle.trim()) {
          return data.handle.trim();
        }
      } catch (err) {
        log('could not parse data-product', err, item);
      }
      return null;
    }

    /* ------------------------------------------------------------------ */
    /* Fetching + caching the sticker markup                               */
    /* ------------------------------------------------------------------ */

    // handle -> Promise<string|null>  (outerHTML of the sticker group, or null)
    var stickerCache = new Map();

    function fetchStickerHTML(handle) {
      if (stickerCache.has(handle)) return stickerCache.get(handle);

      var promise = schedule(function () {
        return fetch('/products/' + encodeURIComponent(handle) + '?view=data', {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + handle);
            return res.text();
          })
          .then(function (html) {
            // DOMParser is inert: no scripts run, no images/CSS are requested.
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var sticker = doc.querySelector(STICKER_SELECTOR);
            return sticker ? sticker.outerHTML : null;
          });
      }).catch(function (err) {
        log('fetch failed', err);
        return null;
      });

      stickerCache.set(handle, promise);
      return promise;
    }

    /* ------------------------------------------------------------------ */
    /* Insertion                                                           */
    /* ------------------------------------------------------------------ */

    // The image wrapper is sometimes rendered a tick after the item itself.
    function waitForTarget(item) {
      return new Promise(function (resolve) {
        var tries = 0;
        (function check() {
          var el = item.querySelector(TARGET_SELECTOR);
          if (el || ++tries >= TARGET_WAIT_TRIES || !item.isConnected) {
            resolve(el);
          } else {
            setTimeout(check, TARGET_WAIT_INTERVAL);
          }
        })();
      });
    }

    function processItem(item) {
      var handle = getHandle(item);
      if (!handle) return;

      // Already done for this exact handle (guards re-entry and the case where
      // Boost reuses a node but swaps in a different product).
      if (item.getAttribute(STATE_ATTR) === handle) return;
      item.setAttribute(STATE_ATTR, handle);

      fetchStickerHTML(handle).then(function (html) {
        if (!html || !item.isConnected) return;
        if (item.getAttribute(STATE_ATTR) !== handle) return; // node got recycled mid-flight

        return waitForTarget(item).then(function (target) {
          if (!target || !item.isConnected) return;
          if (target.querySelector(STICKER_SELECTOR)) return; // don't double-insert
          target.insertAdjacentHTML('beforeend', html);
          log('inserted sticker for', handle);
        });
      });
    }

    function scan(root) {
      if (!root || root.nodeType !== 1) return;
      if (root.matches && root.matches(ITEM_SELECTOR)) processItem(root);
      var found = root.querySelectorAll ? root.querySelectorAll(ITEM_SELECTOR) : [];
      for (var i = 0; i < found.length; i++) processItem(found[i]);
    }

    /* ------------------------------------------------------------------ */
    /* Observer                                                            */
    /* ------------------------------------------------------------------ */

    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var mutation = mutations[m];

        if (mutation.type === 'childList') {
          for (var n = 0; n < mutation.addedNodes.length; n++) {
            scan(mutation.addedNodes[n]);
          }
        } else if (mutation.type === 'attributes') {
          // Boost sometimes recycles a card node and just swaps data-product.
          var target = mutation.target;
          if (target.matches && target.matches(ITEM_SELECTOR)) processItem(target);
        }
      }
    });

    function start() {
      scan(document.body);           // anything already in the DOM
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-product']
      });
      log('observing');
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }

    // Optional: expose a manual re-scan for debugging / other scripts.
    window.boostStickers = { scan: scan, cache: stickerCache };
  })();
});